import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import type { MetricItem } from "../../Common/types";
import { status as statusKeys } from "../../Common/types";
import {
  parseHealthProfileData,
  parseHealthProfileDataAsString,
} from "@/features/HealthScore/utils/parseHealthProfileData";

function getItemByType(
  items: HealthProfileSuccessResponseData["items"],
  questionType: string
) {
  return items.find((i) => i.questionType === questionType);
}

/** Exercise: >150 vigorous / moderate+weights / >10k GREEN; >150 moderate or 3501-9999 AMBER; 0-150 or ≤3500 RED. Values show units in minutes. */
function getExerciseStatusAndValue(data: string | null | undefined): {
  status: string;
  value: string;
} {
  const parsed = parseHealthProfileData(data);
  if (parsed == null) {
    return { status: statusKeys.needAttention, value: "—" };
  }
  const obj =
    typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  let rawValue: string;
  if (obj?.value != null) {
    rawValue = String(obj.value);
  } else {
    rawValue = "";
  }
  const valueBand = rawValue.toLowerCase();
  const performedStrength = Boolean(
    obj?.PerformedStrengthExercises ?? obj?.performedStrengthExercises
  );
  const intensity = obj?.Intensity ?? obj?.intensity;
  const withMinutes = (v: string) =>
    v && !v.includes("minute") ? `${v} minutes` : v || "—";
  // GREEN: >150 vigorous, or >150 moderate + weights, or >10k steps
  if (
    performedStrength &&
    (valueBand.includes("150") || valueBand.includes("300"))
  ) {
    return {
      status: statusKeys.healthy,
      value: withMinutes(valueBand),
    };
  }
  if (intensity && String(intensity).toLowerCase() === "vigorous") {
    return {
      status: statusKeys.healthy,
      value: withMinutes(valueBand),
    };
  }
  if (valueBand.includes("10000") || valueBand === ">10k") {
    return { status: statusKeys.healthy, value: valueBand };
  }
  // AMBER: >150 moderate or 3501-9999 steps
  if (
    valueBand === "150-300" ||
    valueBand === "351-9999" ||
    valueBand.includes("3501")
  ) {
    return {
      status: statusKeys.needAttention,
      value: withMinutes(valueBand),
    };
  }
  // RED: 0-150 or ≤3500 – show as "<150 minutes"
  if (
    valueBand &&
    (valueBand.includes("<150") ||
      valueBand === "0-150" ||
      valueBand === "0-3500")
  ) {
    return { status: statusKeys.needsSupport, value: "<150 minutes" };
  }
  if (!valueBand) {
    return { status: statusKeys.needAttention, value: "—" };
  }
  return {
    status: statusKeys.needAttention,
    value: withMinutes(valueBand),
  };
}

/** Sleep: 8-9 hours GREEN, >9 AMBER, <7 (including 7) RED. Always show unit "hours". When value is 7, display "<7 hours" and RAG red. */
function getSleepStatusAndValue(data: string | null | undefined): {
  status: string;
  value: string;
} {
  const s = parseHealthProfileDataAsString(data);
  if (s == null || s === "") {
    return { status: statusKeys.needAttention, value: "—" };
  }
  const hours = Number(s);
  if (!Number.isFinite(hours)) {
    return { status: statusKeys.needAttention, value: s };
  }
  if (hours <= 7) {
    return {
      status: statusKeys.needsSupport,
      value: hours === 7 ? "<7 hours" : `${hours} hours`,
    };
  }
  if (hours >= 8 && hours <= 9) {
    return { status: statusKeys.healthy, value: `${hours} hours` };
  }
  return { status: statusKeys.needAttention, value: `${hours} hours` };
}

/** Diet: 2 meals GREEN, 1 meal AMBER, none RED */
function getDietStatusAndValue(data: string | null | undefined): {
  status: string;
  value: string;
} {
  const s = parseHealthProfileDataAsString(data);
  if (s == null || s === "") {
    return { status: statusKeys.needsSupport, value: "—" };
  }
  const n = Number(s);
  if (n >= 2) {
    return { status: statusKeys.healthy, value: "2+ meals" };
  }
  if (n === 1) {
    return { status: statusKeys.needAttention, value: "1 meal" };
  }
  return { status: statusKeys.needsSupport, value: "None" };
}

/** Alcohol: none GREEN, 1 AMBER, 2 (F) or >2 RED; 2 (M) AMBER */
function getAlcoholStatusAndValue(
  data: string | null | undefined,
  _isFemale?: boolean
): { status: string; value: string } {
  const s = parseHealthProfileDataAsString(data)?.toLowerCase();
  if (s == null || s === "") {
    return { status: statusKeys.needAttention, value: "—" };
  }
  if (s === "none" || s === "0") {
    return { status: statusKeys.healthy, value: "None" };
  }
  if (s === "1" || s === "1 drink") {
    return { status: statusKeys.needAttention, value: "1 per day" };
  }
  if (s === "2" || s === "2 drinks") {
    return { status: statusKeys.needAttention, value: "2 per day" };
  }
  return { status: statusKeys.needsSupport, value: "2+ per day" };
}

/** Smoking: never/not in 5y GREEN, vape/stopped <5y AMBER, current RED */
function getSmokingStatusAndValue(data: string | null | undefined): {
  status: string;
  value: string;
} {
  const s = parseHealthProfileDataAsString(data)?.toLowerCase();
  if (s == null || s === "") {
    return { status: statusKeys.needAttention, value: "—" };
  }
  if (s === "never" || s === "not in 5 years") {
    return { status: statusKeys.healthy, value: "Never" };
  }
  if (s === "current" || s === "currently smoke") {
    return { status: statusKeys.needsSupport, value: "Current" };
  }
  return { status: statusKeys.needAttention, value: "Stopped / Vape" };
}

export function getLifestyleMetricsFromHealthProfile(
  healthProfile: HealthProfileSuccessResponseData | null
): MetricItem[] {
  if (!healthProfile?.items?.length) {
    return [];
  }

  const items = healthProfile.items;
  const exerciseItem = getItemByType(items, "IntenseExercise");
  const sleepItem = getItemByType(items, "Sleep");
  const dietItem = getItemByType(items, "FruitVegConsumption");
  const alcoholItem = getItemByType(items, "AlcoholConsumption");
  const smokingItem = getItemByType(items, "SmokerStatus");

  const exercise = getExerciseStatusAndValue(exerciseItem?.data?.value);
  const sleep = getSleepStatusAndValue(sleepItem?.data?.value);
  const diet = getDietStatusAndValue(dietItem?.data?.value);
  const alcohol = getAlcoholStatusAndValue(alcoholItem?.data?.value);
  const smoking = getSmokingStatusAndValue(smokingItem?.data?.value);

  return [
    {
      id: "exercise",
      title: "Exercise",
      value: exercise.value,
      status: exercise.status,
      showChevron: true,
      lastUpdated: exerciseItem?.lastUpdated ?? undefined,
    },
    {
      id: "sleep",
      title: "Sleep",
      value: sleep.value,
      status: sleep.status,
      showChevron: true,
      lastUpdated: sleepItem?.lastUpdated ?? undefined,
    },
    {
      id: "diet",
      title: "Diet",
      value: diet.value,
      status: diet.status,
      showChevron: false,
      lastUpdated: dietItem?.lastUpdated ?? undefined,
    },
    {
      id: "alcohol",
      title: "Alcohol",
      value: alcohol.value,
      status: alcohol.status,
      showChevron: false,
      lastUpdated: alcoholItem?.lastUpdated ?? undefined,
    },
    {
      id: "smoking",
      title: "Smoking",
      value: smoking.value,
      status: smoking.status,
      showChevron: false,
      lastUpdated: smokingItem?.lastUpdated ?? undefined,
    },
  ];
}
